import React, { useMemo } from 'react';
import { ResumePreviewProps, WorkExperience, Education, Certification, Project } from '../types';
import { usePreviewSettings } from '../templateSettings';

const formatDateWithFormat = (dateString: string | null, dateFormat?: string, yearOnly?: boolean): string => {
  if (!dateString) return '';
  const date = /^\d{4}-\d{2}$/.test(dateString) ? new Date(`${dateString}-01`) : new Date(dateString);
  if (isNaN(date.getTime())) return "";
  if (yearOnly) return date.getFullYear().toString();
  if (!dateFormat) {
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  }
  const month = date.getMonth() + 1;
  const monthShort = date.toLocaleDateString('en-US', { month: 'short' });
  const monthLong = date.toLocaleDateString('en-US', { month: 'long' });
  const year = date.getFullYear();
  const yearShort = year.toString().slice(-2);
  switch (dateFormat) {
    case '%b %y': return `${monthShort} ${yearShort}`;
    case '%B %Y': return `${monthLong} ${year}`;
    case '%b %Y': return `${monthShort} ${year}`;
    case '%m/%Y': return `${month.toString().padStart(2, '0')}/${year}`;
    case '%m/%y': return `${month.toString().padStart(2, '0')}/${yearShort}`;
    case '%Y': return year.toString();
    default: return `${monthShort} '${yearShort}`;
  }
};

const formatDateRange = (startDate: string | null, endDate: string | null, isCurrent: boolean, dateFormat?: string, yearOnly?: boolean, hideDate?: boolean): string => {
  if (hideDate) return '';
  if (!startDate) return '';
  const start = formatDateWithFormat(startDate, dateFormat, yearOnly);
  if (isCurrent) return `${start} - Present`;
  if (!endDate) return start;
  const end = formatDateWithFormat(endDate, dateFormat, yearOnly);
  return `${start} - ${end}`;
};

interface EditableFieldProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableField: React.FC<EditableFieldProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <span
      className={`resume-editable-field ${isActive ? 'resume-editable-field--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </span>
  );
};

interface EditableBlockProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableBlock: React.FC<EditableBlockProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <div
      className={`resume-editable-block ${isActive ? 'resume-editable-block--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </div>
  );
};

interface EditableLineProps {
  fieldName: string;
  children: React.ReactNode;
  activeField?: string | null;
  onFieldClick?: (fieldName: string) => void;
  className?: string;
}

const EditableLine: React.FC<EditableLineProps> = ({
  fieldName,
  children,
  activeField,
  onFieldClick,
  className = ''
}) => {
  const isActive = activeField === fieldName;
  return (
    <li
      className={`resume-editable-line ${isActive ? 'resume-editable-line--active' : ''} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        onFieldClick?.(fieldName);
      }}
    >
      {children}
    </li>
  );
};

export const AspenTemplate: React.FC<ResumePreviewProps> = ({ data, settings, structures = [], activeField, onFieldClick }) => {
  const {
    personalInfo,
    targetJobTitle,
    professionalSummary,
    workExperiences,
    volunteerExperiences,
    educations,
    certifications,
    projects,
    categorySkills,
    links,
    customSections
  } = data;

  const dateFormat = settings?.date_format;
  const { rootStyle } = usePreviewSettings(settings);

  const structureTypeToId: Record<string, string> = {
    'TYPE_INFO': 'about',
    'TYPE_WORK_EXPERIENCE': 'experience',
    'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
    'TYPE_EDUCATION': 'education',
    'TYPE_CERTIFICATIONS': 'certifications',
    'TYPE_PROJECTS': 'projects',
    'TYPE_SKILLS': 'skills',
    'TYPE_LINK_BLOCKS': 'links',
  };

  const sectionTitlesMap = useMemo(() => {
    const map: Record<string, string> = {};
    structures.forEach(s => {
      const sectionId = structureTypeToId[s.structure_type];
      if (sectionId) map[sectionId] = s.structure_name;
    });
    return map;
  }, [structures]);

  const getSectionTitle = (sectionId: string, defaultTitle: string): string => {
    return sectionTitlesMap[sectionId] || defaultTitle;
  };

  return (
    <div className="resume-preview resume-aspen" style={rootStyle}>
      <div className="resume-aspen__sidebar">

        <div className="resume-aspen__sidebar-section">
          <h3 className="resume-aspen__sidebar-title">Contact</h3>
          <div className="resume-aspen__contact-info">
            {personalInfo.email && (
              <p>
                <EditableField fieldName="email" activeField={activeField} onFieldClick={onFieldClick}>
                  {personalInfo.email}
                </EditableField>
              </p>
            )}
            {personalInfo.phone && (
              <p>
                <EditableField fieldName="phone" activeField={activeField} onFieldClick={onFieldClick}>
                  {personalInfo.phone}
                </EditableField>
              </p>
            )}
            {personalInfo.location && (
              <p>
                <EditableField fieldName="location" activeField={activeField} onFieldClick={onFieldClick}>
                  {personalInfo.location}
                </EditableField>
              </p>
            )}
            {links?.find(l => l.type === 'linkedin')?.url && (
              <p>
                <EditableField fieldName="linkedin" activeField={activeField} onFieldClick={onFieldClick}>
                  {links.find(l => l.type === 'linkedin')?.url}
                </EditableField>
              </p>
            )}
          </div>
        </div>

        {educations && educations.length > 0 && (
          <div className="resume-aspen__sidebar-section">
            <h3 className="resume-aspen__sidebar-title">{getSectionTitle('education', 'Education')}</h3>
            {educations.map((edu: Education) => (
              <EditableBlock
                key={edu.uid}
                fieldName={`education.${edu.uid}`}
                activeField={activeField}
                onFieldClick={onFieldClick}
                className="resume-aspen__sidebar-entry"
              >
                <p className="resume-aspen__sidebar-entry-title">
                  <EditableField
                    fieldName={`education.${edu.uid}.institution`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                  >
                    {edu.institution}
                  </EditableField>
                </p>
                {edu.degree && (
                  <p className="resume-aspen__sidebar-entry-subtitle">
                    <EditableField
                      fieldName={`education.${edu.uid}.degree`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {edu.degree}
                    </EditableField>
                  </p>
                )}
                {edu.field_of_study && (
                  <p className="resume-aspen__sidebar-entry-detail">
                    <EditableField
                      fieldName={`education.${edu.uid}.field_of_study`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {edu.field_of_study}
                    </EditableField>
                  </p>
                )}
                {edu.graduation_date && (
                  <p className="resume-aspen__sidebar-entry-date">
                    <EditableField
                      fieldName={`education.${edu.uid}.graduation_date`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      Graduation: {formatDateWithFormat(edu.graduation_date, dateFormat)}
                    </EditableField>
                  </p>
                )}
              </EditableBlock>
            ))}
          </div>
        )}

        {certifications && certifications.length > 0 && (
          <div className="resume-aspen__sidebar-section">
            <h3 className="resume-aspen__sidebar-title">{getSectionTitle('certifications', 'Certifications')}</h3>
            {certifications.map((cert: Certification) => (
              <EditableBlock
                key={cert.uid}
                fieldName={`certification.${cert.uid}`}
                activeField={activeField}
                onFieldClick={onFieldClick}
                className="resume-aspen__sidebar-entry"
              >
                <p className="resume-aspen__sidebar-entry-title">
                  <EditableField
                    fieldName={`certification.${cert.uid}.certification_name`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                  >
                    {cert.certification_name}
                  </EditableField>
                </p>
                <p className="resume-aspen__sidebar-entry-detail">
                  <EditableField
                    fieldName={`certification.${cert.uid}.issued_at`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                  >
                    {[
                      cert.issued_at && formatDateWithFormat(cert.issued_at, dateFormat),
                      cert.organization_name || null,
                      cert.expires_at && `Expires: ${formatDateWithFormat(cert.expires_at, dateFormat)}`,
                      cert.credential_id && `ID: ${cert.credential_id}`,
                    ].filter(Boolean).join(' | ')}
                  </EditableField>
                </p>
                {cert.description && (
                  <p className="resume-aspen__sidebar-entry-subtitle">
                    <EditableField
                      fieldName={`certification.${cert.uid}.description`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {cert.description}
                    </EditableField>
                  </p>
                )}
              </EditableBlock>
            ))}
          </div>
        )}

        {links && links.length > 0 && (
          <div className="resume-aspen__sidebar-section">
            <h3 className="resume-aspen__sidebar-title">{getSectionTitle('links', 'Links')}</h3>
            {links.map((link, idx) => (
              <EditableBlock
                key={idx}
                fieldName={`link.${idx}`}
                activeField={activeField}
                onFieldClick={onFieldClick}
                className="resume-aspen__sidebar-entry"
              >
                <p className="resume-aspen__sidebar-entry-title">{link.type}</p>
                <p className="resume-aspen__sidebar-entry-detail">{link.url}</p>
              </EditableBlock>
            ))}
          </div>
        )}

        {categorySkills && Object.values(categorySkills).some(skillsList => skillsList && skillsList.length > 0) && (
          <div className="resume-aspen__sidebar-section">
            <h3 className="resume-aspen__sidebar-title">{getSectionTitle('skills', 'Skills')}</h3>
            <div className="resume-aspen__skills">
              {Object.entries(categorySkills).map(([category, skills]) => (
                skills && skills.length > 0 && (
                  <EditableField
                    key={category}
                    fieldName={`skills.${category}`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                  >
                    <p className="resume-aspen__skill-line">
                      {category !== 'General skills' && <strong>{category}: </strong>}
                      {skills.join(' · ')}
                    </p>
                  </EditableField>
                )
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="resume-aspen__main">
        <div className="resume-aspen__header">
          <h1 className="resume-aspen__name">
            <EditableField fieldName="firstName" activeField={activeField} onFieldClick={onFieldClick}>
              {personalInfo.firstName}
            </EditableField>
            {' '}
            <EditableField fieldName="lastName" activeField={activeField} onFieldClick={onFieldClick}>
              {personalInfo.lastName}
            </EditableField>
          </h1>
          {targetJobTitle && (
            <p className="resume-aspen__job-title">
              <EditableField fieldName="targetJobTitle" activeField={activeField} onFieldClick={onFieldClick}>
                {targetJobTitle}
              </EditableField>
            </p>
          )}
        </div>

        {professionalSummary && (
          <div className="resume-aspen__section">
            <h2 className="resume-aspen__section-title">{getSectionTitle('about', 'Professional Summary')}</h2>
            <EditableBlock fieldName="summary" activeField={activeField} onFieldClick={onFieldClick}>
              <ul className="resume-aspen__list">
                {professionalSummary.split('\n').filter(line => line.trim()).map((line, index) => (
                  <li key={index}>{line.replace(/^[•\-]\s*/, '')}</li>
                ))}
              </ul>
            </EditableBlock>
          </div>
        )}

        {workExperiences && workExperiences.length > 0 && (
          <div className="resume-aspen__section">
            <h2 className="resume-aspen__section-title">{getSectionTitle('experience', 'Experience')}</h2>
            {workExperiences.map((exp: WorkExperience) => (
              <EditableBlock
                key={exp.uid}
                fieldName={`experience.${exp.uid}`}
                activeField={activeField}
                onFieldClick={onFieldClick}
                className="resume-aspen__entry"
              >
                <div className="resume-aspen__entry-header">
                  <div>
                    <EditableField
                      fieldName={`experience.${exp.uid}.job_title`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-aspen__entry-role"
                    >
                      {exp.job_title}
                    </EditableField>
                    <p className="resume-aspen__entry-company">
                      <EditableField
                        fieldName={`experience.${exp.uid}.employer_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {exp.employer_name}
                      </EditableField>
                    </p>
                  </div>
                  <span className="resume-aspen__entry-date">
                    <EditableField
                      fieldName={`experience.${exp.uid}.start_date`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                    </EditableField>
                  </span>
                </div>
                {exp.description && (
                  <EditableField
                    fieldName={`experience.${exp.uid}.description`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-aspen__entry-description"
                  >
                    {exp.description}
                  </EditableField>
                )}
                {exp.achievements && exp.achievements.length > 0 && (
                  <ul className="resume-aspen__list">
                    {exp.achievements.map((achievement, idx) => (
                      <EditableLine
                        key={idx}
                        fieldName={`experience.${exp.uid}.achievement.${idx}`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {achievement}
                      </EditableLine>
                    ))}
                  </ul>
                )}
              </EditableBlock>
            ))}
          </div>
        )}

        {volunteerExperiences && volunteerExperiences.length > 0 && (
          <div className="resume-aspen__section">
            <h2 className="resume-aspen__section-title">{getSectionTitle('volunteer', 'Volunteer Experience')}</h2>
            {volunteerExperiences.map((exp: WorkExperience) => (
              <EditableBlock
                key={exp.uid}
                fieldName={`volunteer.${exp.uid}`}
                activeField={activeField}
                onFieldClick={onFieldClick}
                className="resume-aspen__entry"
              >
                <div className="resume-aspen__entry-header">
                  <div>
                    <EditableField
                      fieldName={`volunteer.${exp.uid}.job_title`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                      className="resume-aspen__entry-role"
                    >
                      {exp.job_title}
                    </EditableField>
                    <p className="resume-aspen__entry-company">
                      <EditableField
                        fieldName={`volunteer.${exp.uid}.employer_name`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {exp.employer_name}
                      </EditableField>
                    </p>
                  </div>
                  <span className="resume-aspen__entry-date">
                    <EditableField
                      fieldName={`volunteer.${exp.uid}.start_date`}
                      activeField={activeField}
                      onFieldClick={onFieldClick}
                    >
                      {formatDateRange(exp.start_date, exp.end_date, exp.is_current, dateFormat, exp.yearOnly, exp.hideDate)}
                    </EditableField>
                  </span>
                </div>
                {exp.achievements && exp.achievements.length > 0 && (
                  <ul className="resume-aspen__list">
                    {exp.achievements.map((achievement, idx) => (
                      <EditableLine
                        key={idx}
                        fieldName={`volunteer.${exp.uid}.achievement.${idx}`}
                        activeField={activeField}
                        onFieldClick={onFieldClick}
                      >
                        {achievement}
                      </EditableLine>
                    ))}
                  </ul>
                )}
              </EditableBlock>
            ))}
          </div>
        )}

        {projects && projects.length > 0 && (
          <div className="resume-aspen__section">
            <h2 className="resume-aspen__section-title">{getSectionTitle('projects', 'Projects')}</h2>
            {projects.map((project: Project) => (
              <EditableBlock
                key={project.uid}
                fieldName={`project.${project.uid}`}
                activeField={activeField}
                onFieldClick={onFieldClick}
                className="resume-aspen__entry"
              >
                <div className="resume-aspen__entry-header">
                  <EditableField
                    fieldName={`project.${project.uid}.project_name`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-aspen__entry-role"
                  >
                    {project.project_name}
                  </EditableField>
                  {project.start_date && (
                    <span className="resume-aspen__entry-date">
                      {project.is_current
                        ? `${formatDateWithFormat(project.start_date, dateFormat)} - Present`
                        : project.end_date
                          ? `${formatDateWithFormat(project.start_date, dateFormat)} - ${formatDateWithFormat(project.end_date, dateFormat)}`
                          : formatDateWithFormat(project.start_date, dateFormat)}
                    </span>
                  )}
                </div>
                {project.url && (
                  <EditableField
                    fieldName={`project.${project.uid}.url`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-aspen__entry-company"
                  >
                    {project.url}
                  </EditableField>
                )}
                {project.description && (
                  <EditableField
                    fieldName={`project.${project.uid}.description`}
                    activeField={activeField}
                    onFieldClick={onFieldClick}
                    className="resume-aspen__entry-description"
                  >
                    {project.description}
                  </EditableField>
                )}
                {project.achievements && project.achievements.length > 0 && (
                  <ul className="resume-aspen__list">
                    {project.achievements.map((achievement, idx) => (
                      <li key={idx}>{achievement}</li>
                    ))}
                  </ul>
                )}
              </EditableBlock>
            ))}
          </div>
        )}

        {customSections?.map((cs) => (
          <div key={cs.id} className="resume-aspen__section">
            <h2 className="resume-aspen__section-title">{cs.title}</h2>
            {cs.layout === 'stacked' ? (
              cs.entries.map((entry) => (
                <div key={entry.id} className="resume-aspen__entry">
                  <div className="resume-aspen__entry-header">
                    <span className="resume-aspen__entry-role">{entry.title}</span>
                    {entry.date && !entry.hideDate && (
                      <span className="resume-aspen__entry-date">
                        {entry.yearOnly
                          ? new Date(entry.date).getFullYear()
                          : formatDateWithFormat(entry.date, dateFormat)}
                        {entry.endDate && !entry.hideEndDate && (
                          <> - {entry.yearOnly
                            ? new Date(entry.endDate).getFullYear()
                            : formatDateWithFormat(entry.endDate, dateFormat)}</>
                        )}
                      </span>
                    )}
                  </div>
                  {entry.description && (
                    <span className="resume-aspen__entry-description">{entry.description}</span>
                  )}
                </div>
              ))
            ) : (
              <ul className="resume-aspen__list">
                {cs.entries.map((entry) => (
                  <li key={entry.id}>
                    <strong>{entry.title}</strong>
                    {entry.description && `: ${entry.description}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
